import { Fragment, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Button,
    Grid,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { URLHandler } from "src/services/URLHandler";
import { useGetOneApplication, useGetApplicationWebhooks } from "src/api/client-api/applications/useGetOneApplication";
import { useCreateWebhook } from "src/api/client-api/applications/useCreateWebhook";
import { useDeleteWebhook } from "src/api/client-api/applications/useDeleteWebhook";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "src/utils/useSnackbar";
import { SecretKeyField } from "./components/SecretKeyField";
import { formatDate } from "src/utils/formatting";
import { FoodsIcon } from "@foodpilot/foods";
import { WebhookModal, type Webhook } from "./components/WebhookModal";
import { RotateSecretDialog } from "./components/RotateSecretDialog";

export const ApplicationEdit = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();

    const applicationQuery = useGetOneApplication(id);
    const webhooksQuery = useGetApplicationWebhooks(id);
    const createWebhookMutation = useCreateWebhook();
    const deleteWebhookMutation = useDeleteWebhook();

    const application = applicationQuery.application;
    const isLoadingApp = applicationQuery.isLoading;
    const appError = applicationQuery.error;

    const webhooks = webhooksQuery.webhooks || [];
    const isLoadingWebhooks = webhooksQuery.isLoading;
    const webhooksError = webhooksQuery.error;

    const isCreatingWebhook = createWebhookMutation.isLoading;
    const isDeletingWebhook = deleteWebhookMutation.isLoading;

    const [webhookModalOpen, setWebhookModalOpen] = useState(false);
    const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [webhookToDelete, setWebhookToDelete] = useState<string | null>(null);
    const [rotateSecretDialogOpen, setRotateSecretDialogOpen] = useState(false);

    const handleCreateWebhook = () => {
        setEditingWebhook(null);
        setWebhookModalOpen(true);
    };

    const handleEditWebhook = (webhook: Webhook) => {
        setEditingWebhook(webhook);
        setWebhookModalOpen(true);
    };

    const handleWebhookModalSuccess = () => {
        webhooksQuery.refetch();
    };

    const handleDeleteClick = (webhookId: string) => {
        setWebhookToDelete(webhookId);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!webhookToDelete || !id) return;

        try {
            await deleteWebhookMutation.mutateAsync({ applicationId: id, webhookId: webhookToDelete });

            setSnackbarProps({
                message: t("applications.webhookDeleted", "Webhook deleted successfully"),
                type: "success",
            });

            setDeleteConfirmOpen(false);
            setWebhookToDelete(null);
        } catch (err: any) {
            setSnackbarProps({
                message: t("applications.webhookDeleteError", "Failed to delete webhook"),
                type: "error",
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setWebhookToDelete(null);
    };

    const handleBack = () => navigate(URLHandler.settings.applications.list());

    if (isLoadingApp || isLoadingWebhooks) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (appError || webhooksError) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                    {t("applications.error", "Error")}:{" "}
                    {(appError as Error)?.message || (webhooksError as Error)?.message}
                </Alert>
                <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBack} sx={{ mt: 2 }}>
                    {t("applications.backToList", "Back to Applications")}
                </Button>
            </Box>
        );
    }

    if (!application) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {t("applications.notFound", "Application not found")}
                </Alert>
                <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBack} sx={{ mt: 2 }}>
                    {t("applications.backToList", "Back to Applications")}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                        onClick={handleBack}
                        sx={{ mr: 2 }}
                        aria-label={t("applications.back", "Back to applications list")}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4">
                        {t("applications.editTitle", "Edit Application")}: {application.name}
                    </Typography>
                </Box>
            </Box>
            {/* Application Details */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    {t("applications.details", "Application Details")}
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("applications.name", "Name")}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {application.name}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("applications.description", "Description")}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {application.description || t("applications.noDescription", "No description provided")}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("applications.appId", "App ID")}
                            </Typography>
                            <SecretKeyField secretKey={application.id} />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("applications.secretKey", "App Secret Key")}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t(
                                        "applications.secretNotDisplayed",
                                        "For security, this value is only shown once, right after creation or regeneration.",
                                    )}
                                </Typography>
                                <Button size="small" variant="outlined" onClick={() => setRotateSecretDialogOpen(true)}>
                                    {t("applications.regenerateSecret", "Regenerate secret")}
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("applications.createdAt", "Created At")}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {formatDate(application.createdAt)}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
            {/* Webhooks Section */}
            <Paper
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Box>
                        <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", marginBottom: "8px" }}>
                            {t("applications.webhooks", "Webhooks")}
                        </Typography>
                        <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#50504D" }}>
                            {t("applications.webhooksDescription", "Manage webhooks for this application.")}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<FoodsIcon icon="plus" size={1} />}
                        onClick={handleCreateWebhook}
                        sx={{
                            borderRadius: "44px",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            lineHeight: "20px",
                            padding: "0px 20px 0px 16px",
                            height: "40px",
                            backgroundColor: "primary.main",
                            "&:hover": {
                                backgroundColor: "primary.dark",
                            },
                        }}
                    >
                        {t("applications.addWebhook", "Add Webhook")}
                    </Button>
                </Box>

                {/* Content area */}
                <Box sx={{ padding: "0px 32px 24px" }}>
                    {webhooks.length === 0 ?
                        <Box
                            sx={{
                                height: "130px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography
                                variant="h3"
                                sx={{ lineHeight: "1.6em", color: "#393A36", textAlign: "center" }}
                            >
                                {t("applications.noWebhooks", "No webhooks found for this application.")}
                            </Typography>
                        </Box>
                    :   <Paper
                            sx={{
                                border: "1px solid #E7E6E4",
                                borderRadius: "8px",
                                overflow: "hidden",
                            }}
                        >
                            <Table sx={{ "& .MuiTableCell-root": { border: "none" } }}>
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            backgroundColor: "#FEFCFB",
                                            "& .MuiTableCell-root": {
                                                borderBottom: "1px solid #E7E6E4",
                                                borderRight: "1px solid #E7E6E4",
                                                padding: "0px 14px 0px 20px",
                                                height: "48px",
                                                "&:last-child": {
                                                    borderRight: "none",
                                                },
                                            },
                                        }}
                                    >
                                        <TableCell sx={{ width: "200px" }}>
                                            <Typography
                                                variant="small-medium"
                                                sx={{ lineHeight: "20px", color: "#767673" }}
                                            >
                                                {t("applications.webhookName", "Name")}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ width: "150px" }}>
                                            <Typography
                                                variant="small-medium"
                                                sx={{ lineHeight: "20px", color: "#767673" }}
                                            >
                                                {t("applications.eventName", "Event")}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ width: "250px" }}>
                                            <Typography
                                                variant="small-medium"
                                                sx={{ lineHeight: "20px", color: "#767673" }}
                                            >
                                                {t("applications.endpoint", "Endpoint")}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ width: "80px" }}>
                                            <Typography
                                                variant="small-medium"
                                                sx={{ lineHeight: "20px", color: "#767673" }}
                                            >
                                                {t("applications.method", "Method")}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ width: "80px" }}>
                                            <Typography
                                                variant="small-medium"
                                                sx={{ lineHeight: "20px", color: "#767673" }}
                                            >
                                                {t("applications.status", "Status")}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: "100px",
                                                minWidth: "100px",
                                                maxWidth: "100px",
                                            }}
                                        >
                                            <Typography
                                                variant="small-medium"
                                                sx={{ lineHeight: "20px", color: "#767673" }}
                                            >
                                                Actions
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {webhooks.map((webhook, index) => (
                                        <Fragment key={webhook.id}>
                                            <TableRow
                                                sx={{
                                                    "& .MuiTableCell-root": {
                                                        borderRight: "1px solid #E7E6E4",
                                                        padding: "0px 14px 0px 20px",
                                                        height: "58px",
                                                        "&:last-child": {
                                                            borderRight: "none",
                                                            padding: "0px 8px",
                                                            width: "100px",
                                                            minWidth: "100px",
                                                            maxWidth: "100px",
                                                        },
                                                    },
                                                    "&:hover": {
                                                        backgroundColor: "#F9F9F8",
                                                    },
                                                }}
                                            >
                                                <TableCell
                                                    sx={{
                                                        padding: "0px 20px",
                                                        height: "58px",
                                                        borderBottom: "none",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="small-medium"
                                                        sx={{ lineHeight: "22px", color: "#1E1E1E" }}
                                                    >
                                                        {webhook.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: "none" }}>
                                                    <Typography
                                                        variant="small-medium"
                                                        sx={{ lineHeight: "22px", color: "#393A36" }}
                                                    >
                                                        {webhook.eventName || "N/A"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: "none" }}>
                                                    <Typography
                                                        variant="small-medium"
                                                        sx={{
                                                            lineHeight: "22px",
                                                            color: "#393A36",
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            maxWidth: "200px",
                                                        }}
                                                        title={webhook.endpoint}
                                                    >
                                                        {webhook.endpoint || "N/A"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: "none" }}>
                                                    <Typography
                                                        variant="small-medium"
                                                        sx={{ lineHeight: "22px", color: "#393A36" }}
                                                    >
                                                        {webhook.method || "POST"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: "none" }}>
                                                    <Typography
                                                        variant="small-medium"
                                                        sx={{
                                                            lineHeight: "22px",
                                                            color: webhook.active ? "primary.main" : "#767673",
                                                        }}
                                                    >
                                                        {webhook.active ?
                                                            t("applications.active", "Active")
                                                        :   t("applications.inactive", "Inactive")}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        borderBottom: "none",
                                                    }}
                                                >
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Box
                                                            component="button"
                                                            onClick={() => handleEditWebhook(webhook)}
                                                            sx={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                                transition: "background-color 0.2s",
                                                                "&:hover": {
                                                                    backgroundColor: "#F5F5F5",
                                                                },
                                                            }}
                                                            title={t("applications.editWebhook", "Edit webhook")}
                                                        >
                                                            <FoodsIcon icon="edit" size={1} />
                                                        </Box>
                                                        <Box
                                                            component="button"
                                                            onClick={() => handleDeleteClick(webhook.id)}
                                                            sx={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                                transition: "background-color 0.2s",
                                                                "&:hover": {
                                                                    backgroundColor: "#F5F5F5",
                                                                },
                                                            }}
                                                            title={t("applications.deleteWebhook", "Delete webhook")}
                                                        >
                                                            <FoodsIcon icon="delete" size={1} />
                                                        </Box>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                            {/* Separator line */}
                                            {index < webhooks.length - 1 && (
                                                <TableRow>
                                                    <TableCell colSpan={6} sx={{ padding: "0px 24px", height: "1px" }}>
                                                        <Box
                                                            sx={{
                                                                height: "1px",
                                                                backgroundColor: "#E7E6E4",
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    }
                </Box>
            </Paper>
            {/* Webhook Modal */}
            {webhookModalOpen && (
                <WebhookModal
                    key={editingWebhook?.id ?? "create"}
                    open
                    onClose={() => setWebhookModalOpen(false)}
                    onSuccess={handleWebhookModalSuccess}
                    editingWebhook={editingWebhook}
                    applicationId={id || ""}
                />
            )}
            {/* Delete Webhook Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} aria-labelledby="delete-webhook-dialog-title">
                <DialogTitle id="delete-webhook-dialog-title">
                    {t("applications.confirmDeleteWebhook", "Confirm Delete Webhook")}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {t(
                            "applications.deleteWebhookWarning",
                            "Are you sure you want to delete this webhook? This action cannot be undone.",
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={isDeletingWebhook}>
                        {t("applications.cancel", "Cancel")}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        startIcon={
                            isDeletingWebhook ? <CircularProgress size={20} /> : <FoodsIcon icon="delete" size={1} />
                        }
                        disabled={isDeletingWebhook}
                    >
                        {t("applications.delete", "Delete")}
                    </Button>
                </DialogActions>
            </Dialog>
            <RotateSecretDialog
                open={rotateSecretDialogOpen}
                applicationId={id || ""}
                onClose={() => setRotateSecretDialogOpen(false)}
            />
        </Box>
    );
};
